import {useCallback, useState} from "react";
import {http as httpApiGateway} from "../../../services/api/restApiClient";
import {withRetryOn504} from "../../../services/api/retry";

// API Gateway enforces a 29s integration timeout, but the underlying Lambda
// runs up to 120s and writes the generated PDF to a deterministic S3 cache key
// even after the gateway gives up. Retrying the same call on 504 hits the
// now-populated cache.

export const useGeneratePdf = ({
  pdfDocumentName,
  admissionId,
  hasScrollEnforcementFeatureFlag,
}: {
  pdfDocumentName: string;
  admissionId: string | null;
  hasScrollEnforcementFeatureFlag: boolean;
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const generatePdf = useCallback(async () => {
    if (!hasScrollEnforcementFeatureFlag || isGenerating || !admissionId)
      return;

    setIsGenerating(true);
    try {
      // Step 1: Call Lambda to generate PDF and get presigned URL.
      const lambdaResponse = await withRetryOn504(() =>
        httpApiGateway.get(`/pdf/admissions/${admissionId}/generate`, {
          type: "full",
        }),
      );
      const downloadUrl = (lambdaResponse.data as {downloadUrl?: string})
        ?.downloadUrl;
      if (!downloadUrl) {
        throw new Error("Download URL not found in response");
      }

      // Step 2: Fetch the actual PDF bytes from S3 via the presigned URL
      const pdfResponse = await fetch(downloadUrl);
      if (!pdfResponse.ok) {
        throw new Error("Failed to fetch PDF from S3");
      }

      const blob = await pdfResponse.blob();
      const file = new File([blob], pdfDocumentName, {type: "application/pdf"});

      setFile(file);
    } catch (error) {
      setFile(null);
    } finally {
      setIsGenerating(false);
    }
  }, [
    admissionId,
    pdfDocumentName,
    isGenerating,
    hasScrollEnforcementFeatureFlag,
  ]);

  return {
    isGenerating,
    file,
    generatePdf,
  };
};

export const downloadPdf = (file: File, pdfDocumentName: string) => {
  if (!file) return;

  const url = window.URL.createObjectURL(file as File);
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.download = pdfDocumentName;
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  window.URL.revokeObjectURL(url);
};
